import React, { useState } from "react";
import I18n from "../../../../../helpers/I18n";
import { ValidatorForm } from "react-form-validator-core";
import InputValidator from "../../../../../components/common/InputValidator";
import { GoogleCaptchaV3Service } from "../../../../../api/Google/captchaV3.service";
import httpClient from "../../../../../api/httpClient";
import Loading from "../../../../../components/common/Loading";
import { camelToSnakeCase } from "../../../../../helpers/StringHelper";
import PhoneCountryInput from "../../../../../components/common/inputs/PhoneCountryInput/PhoneCountryInput";
import { User } from "../../../../../api/Users/Types";
import { Checkbox } from "@/components/ui/checkbox";
import { LocalizedPaths } from "../../../../../types/Common";

interface Props {
  readonly formId: string;
  readonly formActionPath: string;
  readonly localeName: string;
  readonly currentUser: User;
  readonly captchaKey: string;
  readonly year?: number;
  readonly explicitMarketingConsent?: boolean;
  readonly explicitTermsConsent?: boolean;
  readonly featuredImage: string;
  readonly featuredImageSuccess?: string;
  readonly featuredImageAlt: string;
  readonly localizedPaths: LocalizedPaths;
}

const GenericFormDownload: React.FC<Props> = ({
  formId,
  formActionPath,
  localeName,
  currentUser,
  captchaKey,
  year,
  explicitMarketingConsent,
  explicitTermsConsent,
  featuredImage,
  featuredImageSuccess,
  featuredImageAlt,
  localizedPaths,
}) => {
  const FORM_DEFAULT_STATE = {
    firstName: currentUser?.first_name || "",
    lastName: currentUser?.last_name || "",
    email: currentUser?.email || "",
    industry: "",
    profession: "",
    company: "",
    phone: currentUser?.phone || "",
    explicitMarketingConsent: explicitMarketingConsent ? false : null,
    explicitTermsConsent: explicitTermsConsent ? false : null,
  };

  const [formSuccess, setFormSuccess] = useState<boolean>(false);

  // HubSpot form integration
React.useEffect(() => {
  const scriptId = "hs-forms-script";
  const existingScript = document.getElementById(scriptId);

  const getHubspotFormId = () => {
    if (formId === 'diy-industrial-automation-report-form') {
      if (year === 2023) return "842c598e-ebed-451f-b3a5-061803f7f36b";
      if (year === 2024) return "651c8210-adf3-4183-9dec-4b1c9656ad90";
    }
    if (formId === 'palletizer-buyers-guide-form') return "2e4f9503-711f-4d1f-a0f4-d7c166d6d499"
    return null;
  };

  const hubspotFormId = getHubspotFormId();

  if (!hubspotFormId) return;

  const createForm = () => {
    if (window.hbspt && window.hbspt.forms) {
      window.hbspt.forms.create({
        portalId: "48496580",
        formId: hubspotFormId,
        region: "na1",
        target: "#hubspot-form-container",
      });
    }
  };

  if (!existingScript) {
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://js.hsforms.net/forms/embed/v2.js";
    script.async = true;
    script.charset = "utf-8";
    script.onload = createForm;
    document.body.appendChild(script);
  } else {
    createForm();
  }
}, [year]);


  if (formSuccess) {
    return (
      <div className="generic-form-download">
        <div className="generic-form-download__cover">
          <img
            src={featuredImageSuccess || featuredImage}
            alt={featuredImageAlt}
          />
        </div>

        <div className="generic-form-download__form generic-form-download__form--success">
          <div className="generic-form-download__form-subtitle">
            {I18n.t(
              `views.landing_pages.marketing_landing_pages.${localeName}.components.form_success.subtitle`,
              {
                year: year,
              },
            )}
          </div>
          <h1 className="generic-form-download__form-heading">
            {I18n.t(
              `views.landing_pages.marketing_landing_pages.${localeName}.components.form_success.heading`,
            )}
          </h1>
          {I18n.t(
            `views.landing_pages.marketing_landing_pages.${localeName}.components.form_success.paragraphs`,
            {
              year: year,
            },
          ).map((paragraph, index) => (
            <p className="generic-form-download__form-paragraph" key={index}>
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="generic-form-download">
      <div className="generic-form-download__cover">
        <img src={featuredImage} alt={featuredImageAlt} />
      </div>

      <div className="generic-form-download__form">
        <h1 className="generic-form-download__form-heading">
          {I18n.t(
            `views.landing_pages.marketing_landing_pages.${localeName}.components.form.heading`,
            { year: year },
          )}
        </h1>
        <p className="generic-form-download__form-paragraph">
          {I18n.t(
            `views.landing_pages.marketing_landing_pages.${localeName}.components.form.paragraph`,
          )}
        </p>
        <div id="hubspot-form-container" />
      </div>
    </div>
  );
};

export default GenericFormDownload;
